import Bullets from '@atoms/Bullets/Bullets';
import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import NavButton from '@atoms/NavButton/NavButton';
import { IImage } from '@models/IImage';
import { ILink } from '@models/ILink';
import { IMeta } from '@models/IMeta';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import HorizontalDirectionEnum from '@models/enums/HorizontalDirectionEnum';
import Carousel from '@molecules/Carousel/Carousel';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import duplicateArrayItems from '@utils/helpers/duplicateArrayItems';
import updateDataLayer from '@utils/helpers/gtm';
import slugify from '@utils/helpers/slugify';
import useBreakpoint from '@utils/hooks/useBreakpoint';
import useIsTouchDevice from '@utils/hooks/useIsTouchDevice';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Swiper from 'swiper';
import { SwiperSlide } from 'swiper/react';

interface ContentItem {
  readonly title: string;
  readonly media?: IImage;
  readonly cta: ILink;
  readonly tags: string[];
}

interface Props {
  readonly title?: string;
  readonly subTitle?: string;
  readonly anchorId?: string;
  readonly cta?: ILink;
  readonly meta?: IMeta;
  readonly content?: ContentItem[];
}

const FeaturedContentCarousel = ({
  title,
  subTitle,
  cta,
  content,
  meta,
  anchorId = '',
}: Props) => {
  const carouselWrapperRef = useRef<HTMLDivElement | null>(null);
  const [swiperInstance, setSwiperInstance] = useState<Swiper>();
  const screenBreakpoint = useBreakpoint(BreakpointTypeEnum.Screen);
  const viewportBreakpoint = useBreakpoint();
  const [imageLeftOffset, setImageLeftOffset] = useState(0);
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [isSwiperInitialized, setIsSwiperInitialized] = useState(false);
  const navButtonId = `featured-content-carousel-${meta?.contentLink ?? ''}`;
  const isTouchDevice = useIsTouchDevice();
  const filteredContent = useMemo(
    () => content?.filter((item) => item.media) ?? [],
    [content]
  );
  const isZoomedDesktop =
    screenBreakpoint === BreakpointEnum.XLarge &&
    viewportBreakpoint !== BreakpointEnum.XLarge;
  const showNavButtonsOnTop =
    isZoomedDesktop ||
    (screenBreakpoint === BreakpointEnum.Large && !isTouchDevice);
  const hasContent = !!filteredContent?.length;
  const shouldLoop =
    !!screenBreakpoint &&
    screenBreakpoint !== BreakpointEnum.Small &&
    filteredContent.length > 1;
  const duplicatedItems = duplicateArrayItems(
    filteredContent,
    filteredContent.length <= 2 ? 6 : filteredContent.length + 1
  );
  const itemsToDisplay = shouldLoop ? duplicatedItems : filteredContent;
  const hasMoreItemsToShow = itemsToDisplay.length > 1;
  const isInCarouselNavButtonDisplayed =
    screenBreakpoint === BreakpointEnum.XLarge && hasMoreItemsToShow;

  const onResize = useCallback(() => {
    const activeImage = carouselWrapperRef?.current?.querySelectorAll(
      '.swiper-slide-active img'
    )[0];

    if (!activeImage) return;

    const { height, width } = activeImage.getBoundingClientRect();
    const leftOffset = (document.body.clientWidth - width) / 2;

    setImageHeight(height);
    setImageWidth(width);
    setImageLeftOffset(leftOffset);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    // We need to re-initialize the navigation when we're zooming as the nav buttons position is changed
    swiperInstance?.navigation.init();
  }, [showNavButtonsOnTop]);

  return (
    <section
      id={`a-${anchorId}`}
      className="component-padding relative overflow-hidden border-white bg-white text-black-100 dark:border-black-100 dark:bg-black-100 dark:text-white"
      data-testid="CT-42-Featured-Content-Carousel-Block"
    >
      <SoftLoadWrapper>
        <div
          className={classNames({
            'max-md:!pl-5': hasContent,
            container: !hasContent,
          })}
          style={{
            ...(hasContent ? { paddingLeft: imageLeftOffset || '18%' } : {}),
          }}
        >
          <div
            className={classNames('flex', {
              'max-md:!w-full mb-12 max-md:pr-5 xl:mb-10': hasContent,
              'justify-between gap-x-5':
                isZoomedDesktop && viewportBreakpoint !== BreakpointEnum.Small,
              'flex-col':
                isZoomedDesktop && viewportBreakpoint === BreakpointEnum.Small,
            })}
            style={{ ...(hasContent ? { width: imageWidth || '78%' } : {}) }}
          >
            <div
              className={classNames({
                'flex w-full flex-col items-start xl:flex-row xl:items-end xl:justify-between':
                  !isZoomedDesktop,
              })}
            >
              <div>
                {title && (
                  <h2
                    className="mb-2.5"
                    data-epi-property-name={meta?.editHints?.title}
                    data-testid="CT-42-Featured-Content-Carousel-Title"
                  >
                    {title}
                  </h2>
                )}

                {subTitle && (
                  <p
                    className={classNames(
                      'body-one text-black-100 xl:mb-0 dark:text-grey-500',
                      {
                        'mb-4': cta?.url && cta.name,
                      }
                    )}
                    data-epi-property-name={meta?.editHints?.subTitle}
                    data-testid="CT-42-Featured-Content-Carousel-Subtitle"
                  >
                    {subTitle}
                  </p>
                )}
              </div>

              {cta?.url && cta.name && (
                <Button
                  link={cta}
                  className="xl:ml-48"
                  linkCallback={() => {
                    if (pageObject?.pageType === 'HomepagePageViewModel') {
                      updateDataLayer({
                        event: 'selectContent',
                        content_type: 'featured_content_carousel',
                        link_text: cta.name,
                      });
                    }
                  }}
                  dataTestId="CT-42-Featured-Content-Carousel-CTA"
                />
              )}
            </div>

            {showNavButtonsOnTop && (
              <div
                className={classNames(
                  'flex gap-x-[25px]',
                  viewportBreakpoint === BreakpointEnum.Small
                    ? 'mt-5 self-start'
                    : 'self-end'
                )}
              >
                <div
                  className={classNames({
                    hidden: !hasMoreItemsToShow,
                  })}
                >
                  <NavButton
                    direction={HorizontalDirectionEnum.Left}
                    id={navButtonId}
                    disabled={!shouldLoop}
                  />
                </div>

                <div
                  className={classNames({
                    hidden: !hasMoreItemsToShow,
                  })}
                >
                  <NavButton
                    direction={HorizontalDirectionEnum.Right}
                    id={navButtonId}
                    disabled={!shouldLoop}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {hasContent && (
          <div className="w-full max-md:pl-5" ref={carouselWrapperRef}>
            <Carousel
              onResize={onResize}
              spaceBetween={20}
              slidesPerView={1.2}
              navId={navButtonId}
              loop={shouldLoop}
              centeredSlides
              onInit={() => {
                setIsSwiperInitialized(true);
              }}
              onSwiper={setSwiperInstance}
              breakpoints={{
                0: {
                  slidesPerView: 1.3,
                  spaceBetween: 16,
                  centeredSlides: false,
                  slidesOffsetAfter: 20,
                },
                768: {
                  slidesPerView: 1.5,
                  spaceBetween: 20,
                  centeredSlides: true,
                  slidesOffsetAfter: 0,
                },
                1439: {
                  slidesPerView: 1.5,
                  spaceBetween: 150,
                },
              }}
            >
              {!showNavButtonsOnTop && (
                <>
                  <motion.div
                    slot="container-start"
                    className={classNames('absolute z-10', {
                      hidden: !isInCarouselNavButtonDisplayed,
                    })}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{
                      left: imageLeftOffset,
                      top: imageHeight / 2,
                      translateX: '-200%',
                      translateY: '-50%',
                    }}
                  >
                    <NavButton
                      direction={HorizontalDirectionEnum.Left}
                      id={navButtonId}
                      disabled={!shouldLoop}
                    />
                  </motion.div>

                  <motion.div
                    slot="container-end"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{
                      right: imageLeftOffset,
                      top: imageHeight / 2,
                      translateX: '200%',
                      translateY: '-50%',
                    }}
                    className={classNames('absolute z-10', {
                      hidden: !isInCarouselNavButtonDisplayed,
                    })}
                  >
                    <NavButton
                      direction={HorizontalDirectionEnum.Right}
                      id={navButtonId}
                      disabled={!shouldLoop}
                    />
                  </motion.div>
                </>
              )}

              {itemsToDisplay?.length
                ? itemsToDisplay.map((item, index) => (
                    <SwiperSlide
                      key={slugify(`${item.title}-${index}`)}
                      data-epi-property-name={meta?.editHints?.content}
                    >
                      {({ isActive }) => {
                        return (
                          <div
                            className={
                              !isSwiperInitialized
                                ? 'max-md:mr-[24%] md:mx-[18%]'
                                : 'mx-auto'
                            }
                            data-testid={`CT-42-Featured-Content-Carousel-Slide-${index}`}
                          >
                            <Image
                              classes="w-full select-none pointer-events-none"
                              image={item.media}
                              imageClasses="w-full aspect-video object-cover"
                              lazyLoad={false}
                              dataTestId={`CT-42-Featured-Content-Carousel-Slide-Image-${index}`}
                            />
                            <div
                              className={classNames(
                                'mt-5 flex select-none flex-col items-start gap-5 xl:mt-9 xl:flex-row',
                                { 'xl:hidden': !isActive }
                              )}
                            >
                              <div
                                className={classNames(
                                  'flex w-full flex-col gap-5'
                                )}
                              >
                                <Bullets
                                  bullets={item.tags}
                                  textSize="body-two"
                                  dataTestId={`CT-42-Featured-Content-Carousel-Slide-Bullets-${index}`}
                                />
                                <h3
                                  className="h5 whitespace-normal xl:mt-2.5"
                                  data-testid={`CT-42-Featured-Content-Carousel-Slide-Title-${index}`}
                                >
                                  {item.title}
                                </h3>
                              </div>
                              <Button
                                className="shrink-0 xl:ml-48"
                                link={item.cta}
                                secondary
                                dataTestId={`CT-42-Featured-Content-Carousel-Slide-CTA-${index}`}
                                linkCallback={() => {
                                  updateDataLayer({
                                    event: 'selectContent',
                                    content_type: item.cta.name,
                                    link_text: item.title,
                                  });
                                }}
                              />
                            </div>
                          </div>
                        );
                      }}
                    </SwiperSlide>
                  ))
                : null}
            </Carousel>
          </div>
        )}
      </SoftLoadWrapper>
    </section>
  );
};
export default FeaturedContentCarousel;
